/**
 * bank_rules.ts — BANKING, the pure rules (2026-09-24). "Implement loans and banking for your credits."
 *
 * BYTE-IDENTICAL at server/src/lib/bank_rules.ts and client/src/lib/bank_rules.ts (verify:banking diffs them):
 * the client shows exactly what the server will charge. No imports — both sides feed it their own numbers.
 *
 * HOW A BANK WORKS HERE (the research: fractional reserve, net interest margin, Aave's utilization curve, FICO's
 * weights, amortization, secured vs unsecured lending, CDs, deposit insurance, a lender of last resort):
 *   - A bank holds REAL credits in its own vault wallet (a deterministic identity). An account balance is a BOOK
 *     entry — the bank's promise to pay. Deposits and withdrawals move credits between your wallet and the vault;
 *     interest is a book entry only.
 *   - The bank lends out what it holds, keeping a RESERVE RATIO of its deposits in the vault. Utilization U =
 *     loans / (deposits + equity): the borrow rate climbs gently to the kink, then steeply (the curve that keeps a
 *     vault liquid).
 *   - A bank lends its DEPOSITORS' money first (its own capital is the buffer behind it), so savers are paid ONLY from
 *     what borrowers pay: savings rate = the average loan rate × (the share of deposits lent out) × (1 − its margin).
 *     Interest is never minted — the margin is the bank's, and a bank with no borrowers pays nothing (as a real one
 *     does). The borrow curve reads the other utilization — loans against ALL funds — so a well-capitalized bank
 *     lends cheaply.
 *   - Rates are quoted PER WEEK (the city's pulse cadence), in basis points.
 */

export const DAY_S = 86_400;
export const WEEK_S = 7 * DAY_S;

/** The Commons Reserve: bank 1, the central bank with a branch at every town's bank terminal. */
export const RESERVE_BANK_ID = 1;
/** City banks take ids above this (1000 + cityId). */
export const CITY_BANK_ID_BASE = 1000;
/** The Reserve's founding capital, minted once. */
export const RESERVE_SEED = 250_000;
/** Above twice its capital, the Reserve remits its profit to the platform each week (a central bank's remittance). */
export const RESERVE_REMIT_ABOVE = 2 * RESERVE_SEED;

// ─── Rates ─────────────────────────────────────────────────────────────────────────────────────

export interface RateTerms {
  /** The borrow rate at zero utilization (bps per week). */
  baseBps: number;
  /** Added by the kink (bps per week). */
  slope1Bps: number;
  /** Added from the kink to full use (bps per week). */
  slope2Bps: number;
  /** The kink, as a fraction of funds lent. */
  kink: number;
  /** The bank's share of loan interest (0..1) — its margin. */
  reserveFactor: number;
  /** The fraction of deposits the vault must hold (0..1). */
  reserveRatio: number;
}

export const RESERVE_TERMS: RateTerms = { baseBps: 100, slope1Bps: 200, slope2Bps: 1200, kink: 0.8, reserveFactor: 0.3, reserveRatio: 0.1 };

/** A city bank's mayor sets these within bounds. */
export const CITY_BOUNDS = {
  baseBps: [40, 400] as const,
  reserveRatio: [0.1, 0.5] as const,
  reserveFactor: [0.1, 0.5] as const,
  /** The least a city bank may be chartered with (credits from the city treasury). */
  minCapital: 2000,
};
export function cityTerms(baseBps: number, reserveRatio: number, reserveFactor: number): RateTerms {
  return {
    baseBps: clamp(Math.round(baseBps), CITY_BOUNDS.baseBps[0], CITY_BOUNDS.baseBps[1]),
    slope1Bps: RESERVE_TERMS.slope1Bps,
    slope2Bps: RESERVE_TERMS.slope2Bps,
    kink: RESERVE_TERMS.kink,
    reserveFactor: clamp(reserveFactor, CITY_BOUNDS.reserveFactor[0], CITY_BOUNDS.reserveFactor[1]),
    reserveRatio: clamp(reserveRatio, CITY_BOUNDS.reserveRatio[0], CITY_BOUNDS.reserveRatio[1]),
  };
}

export function clamp(v: number, lo: number, hi: number): number {
  if (!(v >= lo)) return lo;
  return v > hi ? hi : v;
}

/** U = loans / (deposits + equity), 0..1. A bank with no funds is fully used. */
export function utilization(loans: number, deposits: number, equity: number): number {
  const funds = deposits + Math.max(0, equity);
  if (!(funds > 0)) return loans > 0 ? 1 : 0;
  return clamp(loans / funds, 0, 1);
}

/** The borrow rate (bps per week) at utilization U — Aave's two-slope curve. */
export function borrowRateBps(u: number, t: RateTerms): number {
  const U = clamp(u, 0, 1);
  if (U <= t.kink) return t.baseBps + t.slope1Bps * (U / t.kink);
  return t.baseBps + t.slope1Bps + t.slope2Bps * ((U - t.kink) / (1 - t.kink));
}

/** The share of deposits out on loan (deposits are lent first; the bank's capital funds the rest). */
export function depositUse(loans: number, deposits: number): number {
  if (!(deposits > 0)) return 0;
  return clamp(loans / deposits, 0, 1);
}

/**
 * The savings rate (bps per week): savers share what borrowers pay on the deposits lent out, less the bank's margin.
 * `avgLoanBps` is the balance-weighted rate of the loans outstanding; `uDeposits` is `depositUse`. What savers earn
 * in total — avgLoan × min(loans, deposits) × (1 − margin) — never exceeds what the borrowers owe.
 */
export function savingsRateBps(avgLoanBps: number, uDeposits: number, t: RateTerms): number {
  return Math.max(0, avgLoanBps * clamp(uDeposits, 0, 1) * (1 - t.reserveFactor));
}

/** A fixed-term deposit pays the savings rate × (1 + 15 % a week of term), fixed when it is opened. */
export const CD_TERMS_DAYS = [3, 7, 14, 28] as const;
export function termDepositRateBps(savingsBps: number, termDays: number): number {
  return savingsBps * (1 + 0.15 * (termDays / 7));
}
/** The value of a term deposit `elapsedS` after opening (simple interest, capped at maturity). */
export function termDepositValue(principal: number, rateBps: number, termDays: number, elapsedS: number): number {
  const t = clamp(elapsedS, 0, termDays * DAY_S);
  return principal * (1 + (rateBps / 10_000) * (t / WEEK_S));
}

/** Continuous growth of an index over `dt` seconds at `rateBps` per week. */
export function growIndex(index: number, rateBps: number, dtS: number): number {
  if (!(dtS > 0) || !(rateBps > 0)) return index;
  return index * Math.exp((rateBps / 10_000) * (dtS / WEEK_S));
}

// ─── Loans ─────────────────────────────────────────────────────────────────────────────────────

export const LOAN_TERMS_DAYS = [3, 7, 14, 21, 28] as const;
/** Installments fall due once a day; unpaid 12 h past due is LATE. */
export const INSTALLMENT_S = DAY_S;
export const LATE_GRACE_S = 12 * 3600;
/** Three installments gone late in a row, or two days past the final due, and the loan defaults. */
export const DEFAULT_MISSED = 3;
export const DEFAULT_PAST_FINAL_S = 2 * DAY_S;
/** A default bars new credit for two weeks. */
export const DEFAULT_BAN_S = 14 * DAY_S;
/** Origination: 1 % of the principal, at least 5 credits, withheld from the disbursement. */
export function originationFee(principal: number): number {
  return Math.max(5, Math.ceil(principal * 0.01));
}
/** A late installment costs 5 % of it, at least 5 credits. */
export function lateFee(installment: number): number {
  return Math.max(5, Math.ceil(installment * 0.05));
}
/** No more than two open loans at once, across every bank. */
export const MAX_OPEN_LOANS = 2;
export const MIN_LOAN = 50;

/** The daily rate of a weekly rate (bps → fraction). */
export function dailyRate(weeklyBps: number): number {
  return weeklyBps / 10_000 / 7;
}

/** The level installment that repays `principal` over `n` periods at rate `r` per period (the mortgage formula). */
export function amortizedPayment(principal: number, r: number, n: number): number {
  if (!(n > 0)) return principal;
  if (!(r > 0)) return principal / n;
  const g = Math.pow(1 + r, n);
  return (principal * r * g) / (g - 1);
}

export interface LoanQuote {
  principal: number;
  termDays: number;
  rateBps: number;
  /** Credits due each day (rounded up). */
  installment: number;
  totalRepaid: number;
  interest: number;
  fee: number;
  /** What reaches your wallet (principal − fee). */
  disbursed: number;
}

/** A borrower's rate: the bank's curve at the post-loan utilization, the credit spread, and a secured discount. */
export function loanRateBps(uAfter: number, t: RateTerms, score: number, secured: boolean): number {
  const r = borrowRateBps(uAfter, t) + creditSpreadBps(score, secured);
  return Math.max(10, Math.round(secured ? r * 0.75 : r));
}

export function quoteLoan(principal: number, termDays: number, rateBps: number): LoanQuote {
  const P = Math.max(0, Math.floor(principal));
  const n = Math.max(1, Math.round(termDays * DAY_S / INSTALLMENT_S));
  const m = amortizedPayment(P, dailyRate(rateBps), n);
  const installment = Math.ceil(m);
  const totalRepaid = Math.round(m * n);
  const fee = originationFee(P);
  return { principal: P, termDays, rateBps, installment, totalRepaid, interest: totalRepaid - P, fee, disbursed: P - fee };
}

// ─── Credit ────────────────────────────────────────────────────────────────────────────────────

export interface CreditFacts {
  onTime: number;
  late: number;
  defaults: number;
  repaidLoans: number;
  /** Credits owed across every bank now. */
  openBalance: number;
  /** Days since the file opened (the first account). */
  ageDays: number;
  /** Loans opened in the last seven days. */
  newLoans7d: number;
  /** Distinct products ever used: savings, term deposit, unsecured, secured, transfer (0..5). */
  kinds: number;
  /** P7 Trustworthiness, 0..100 (neutral 50). */
  trust: number;
  traitor: boolean;
}

export interface CreditScore {
  score: number;
  /** Each factor 0..1 with its FICO weight — the Credit tab draws them. */
  factors: { key: string; label: string; weight: number; value: number }[];
  trustMod: number;
  /** A file with no repaid loan. */
  thin: boolean;
}

/** The yardstick an open balance is measured against (a FICO "credit utilization"). */
export const OWED_YARDSTICK = 5000;

/** FICO's weights: payment history 35 %, amounts owed 30 %, length 15 %, new credit 10 %, mix 10 %. */
export function creditScore(f: CreditFacts): CreditScore {
  const events = f.onTime + f.late + f.defaults;
  const history = events === 0 ? 0.55 : clamp(f.onTime / (f.onTime + 2 * f.late + 12 * f.defaults), 0, 1);
  const owed = 1 - clamp(f.openBalance / OWED_YARDSTICK, 0, 1);
  const length = clamp(f.ageDays / 60, 0, 1);
  const fresh = 1 - clamp(f.newLoans7d / 3, 0, 1);
  const mix = clamp(f.kinds / 4, 0, 1);
  const factors = [
    { key: 'history', label: 'Payment history', weight: 0.35, value: history },
    { key: 'owed', label: 'Amounts owed', weight: 0.30, value: owed },
    { key: 'length', label: 'Length of history', weight: 0.15, value: length },
    { key: 'fresh', label: 'New credit', weight: 0.10, value: fresh },
    { key: 'mix', label: 'Credit mix', weight: 0.10, value: mix },
  ];
  let sum = 0;
  for (const x of factors) sum += x.weight * x.value;
  const trustMod = f.traitor ? -60 : Math.round((clamp(f.trust, 0, 100) - 50) * 0.6);
  const score = Math.round(clamp(300 + 550 * sum + trustMod, 300, 850));
  return { score, factors, trustMod, thin: f.repaidLoans === 0 };
}

export function scoreBand(score: number): string {
  if (score >= 800) return 'Exceptional';
  if (score >= 740) return 'Very good';
  if (score >= 670) return 'Good';
  if (score >= 580) return 'Fair';
  return 'Poor';
}

/** The spread a score adds to the bank's rate (bps per week). A poor score borrows only against collateral. */
export function creditSpreadBps(score: number, secured: boolean): number {
  if (score >= 800) return -20;
  if (score >= 740) return 0;
  if (score >= 670) return 40;
  if (score >= 580) return 100;
  return secured ? 150 : 400;
}

/** The unsecured limit a score earns; a thin file (no loan repaid yet) is capped at 250. */
export function unsecuredLimit(score: number, thin: boolean): number {
  let lim = 0;
  if (score >= 800) lim = 8000;
  else if (score >= 740) lim = 4000;
  else if (score >= 670) lim = 1500;
  else if (score >= 580) lim = 500;
  return thin ? Math.min(lim, 250) : lim;
}
/** An unsecured loan needs a file at least a day old. */
export const THIN_FILE_MIN_AGE_S = DAY_S;

// ─── Collateral (a pawn) ───────────────────────────────────────────────────────────────────────

/** A secured loan lends up to half of what the collateral appraises at. */
export const SECURED_LTV = 0.5;
/** The appraiser's base value by schematic complexity (1..8). */
export const APPRAISAL_BY_COMPLEXITY: Record<number, number> = { 1: 20, 2: 45, 3: 110, 4: 350, 5: 800, 6: 1600, 7: 3200, 8: 6000 };
/** An item's appraisal: its design's base × (0.5 + quality / 1000). */
export function appraiseItem(complexity: number, quality: number): number {
  const c = clamp(Math.round(complexity), 1, 8);
  return Math.max(1, Math.floor(APPRAISAL_BY_COMPLEXITY[c] * (0.5 + clamp(quality, 0, 1000) / 1000)));
}
/** A stack of raw goods appraises at a fifth of a credit a unit. */
export function appraiseStack(qty: number): number {
  return Math.floor(Math.max(0, qty) / 5);
}
export function securedLimit(appraisal: number): number {
  return Math.floor(appraisal * SECURED_LTV);
}

/** A seized pledge is auctioned at a falling fixed price, then scrapped (never sold to the platform: no mint). */
export const REPO_STEPS: readonly { afterS: number; share: number }[] = [
  { afterS: 0, share: 0.8 },
  { afterS: 3 * DAY_S, share: 0.6 },
  { afterS: 7 * DAY_S, share: 0.4 },
];
export const REPO_SCRAP_S = 14 * DAY_S;
export function repoPrice(appraisal: number, ageS: number): number {
  let share = REPO_STEPS[0].share;
  for (const s of REPO_STEPS) if (ageS >= s.afterS) share = s.share;
  return Math.max(1, Math.floor(appraisal * share));
}

// ─── Transfers, boxes, insurance ───────────────────────────────────────────────────────────────

/** A wire to another bank costs 1 % (2..100 credits); a move within one bank is free. */
export function wireFee(amount: number): number {
  return clamp(Math.ceil(amount * 0.01), 2, 100);
}
/** A safe deposit box holds 24 entries; storing costs a 2-credit handling fee. */
export const BOX_CAPACITY = 24;
export const BOX_FEE = 2;
/** The Reserve insures each depositor at a city bank up to 25,000; the city bank pays 5 bps a week of deposits. */
export const INSURED_MAX = 25_000;
export const INSURANCE_PREMIUM_BPS = 5;
/** A city bank's branch: within this of its bank building (its interior stands on the building's spot). */
export const CITY_BRANCH_M = 40;

/** A bank's balance sheet from its parts. */
export interface BankSheet {
  vaultCash: number;
  loans: number;
  deposits: number;
  owesReserve: number;
}
export function equityOf(s: BankSheet): number {
  return s.vaultCash + s.loans - s.deposits - s.owesReserve;
}
/** What the vault may lend or pay out and still hold its reserve. */
export function freeCash(s: BankSheet, t: RateTerms): number {
  return Math.max(0, s.vaultCash - t.reserveRatio * s.deposits);
}
